import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, CheckCircle2, Download, Upload, Folder, Award, Wallet, FileCheck, FileWarning, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PageHeader, KPIGrid, KPICard, EmptyState } from "@/components/app/PageKit";

const PERSONAL_DOCS = [
  { code: "form_16",      label: "Form 16",                 desc: "Annual TDS certificate from employer (Income Tax)",        icon: Award,    yearly: true },
  { code: "form_12bb",    label: "Form 12BB",               desc: "Investment proofs for tax saving (employee declaration)",  icon: FileCheck, yearly: true },
  { code: "salary_cert",  label: "Salary Certificate",      desc: "Income proof for loans, visa, rentals",                    icon: Wallet,   yearly: false },
  { code: "offer_letter", label: "Offer Letter",            desc: "Original offer issued at time of joining",                  icon: FileText, yearly: false },
  { code: "appointment",  label: "Appointment Letter",      desc: "Confirmation of appointment, policy snapshot",              icon: FileText, yearly: false },
  { code: "experience",   label: "Experience Letter",       desc: "Generated on offboarding · pre-approved by HR",             icon: Award,    yearly: false },
  { code: "relieving",    label: "Relieving Letter",        desc: "Final settlement & exit confirmation",                       icon: FileWarning, yearly: false },
  { code: "noc",          label: "No-Objection Certificate",desc: "For visa, side projects, side income",                       icon: FileCheck, yearly: false },
];

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [personal, setPersonal] = useState([]);

  const load = async () => {
    const r = await api.get("/documents");
    setDocs(r.data || []);
    if (user?.employee_id) {
      try {
        const r2 = await api.get(`/employees/${user.employee_id}/documents`);
        setPersonal(r2.data || []);
      } catch { /* employee may not have permission */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.employee_id]);

  const ack = async (id) => {
    try { await api.post(`/documents/${id}/acknowledge`); toast.success("Acknowledged"); load(); } catch { toast.error("Failed"); }
  };

  const byCat = {
    policy:          docs.filter(d => d.category === "policy"),
    form:            docs.filter(d => d.category === "form"),
    letter_template: docs.filter(d => d.category === "letter_template"),
  };

  const personalByCode = Object.fromEntries(personal.map(p => [p.category, p]));

  const Row = (d) => (
    <div key={d.id} className="p-4 flex items-center gap-3 border-b border-slate-100 last:border-0 hover:bg-slate-50/40 transition" data-testid={`doc-${d.id}`}>
      <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0">
        <FileText className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 truncate">{d.title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 capitalize">{d.category?.replace("_", " ")} · v{d.version || 1} · {(d.acknowledged_by || []).length} acknowledged</div>
      </div>
      {d.mandatory && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Mandatory</Badge>}
      <Button variant="outline" size="sm" onClick={() => ack(d.id)} data-testid={`doc-ack-${d.id}`}>
        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Acknowledge
      </Button>
    </div>
  );

  const stats = {
    total: docs.length,
    policies: byCat.policy.length,
    forms: byCat.form.length,
    letters: byCat.letter_template.length,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="documents-page">
      <PageHeader
        category="Knowledge"
        title="Documents"
        description="Policies, forms, employment letters and your personal tax documents (Form 16, 12BB, salary certificates)"
        primaryAction={["super_admin", "hr_admin"].includes(user?.role) ? {
          label: "Upload",
          icon: Upload,
          onClick: () => toast.info("Upload flow — connect to documents API"),
          testid: "doc-upload-btn",
        } : undefined}
      />

      <KPIGrid cols={4}>
        <KPICard icon={Folder}    tone="violet"  label="All Documents" value={stats.total} testid="kpi-total" />
        <KPICard icon={FileCheck} tone="cyan"    label="Policies"      value={stats.policies} testid="kpi-policy" />
        <KPICard icon={FileText}  tone="emerald" label="Forms"         value={stats.forms} testid="kpi-form" />
        <KPICard icon={Award}     tone="amber"   label="Letter Templates" value={stats.letters} testid="kpi-letter" />
      </KPIGrid>

      <Tabs defaultValue="personal">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="personal">My Documents</TabsTrigger>
          <TabsTrigger value="policy">Policies ({byCat.policy.length})</TabsTrigger>
          <TabsTrigger value="form">Forms ({byCat.form.length})</TabsTrigger>
          <TabsTrigger value="letter_template">Letter Templates ({byCat.letter_template.length})</TabsTrigger>
        </TabsList>

        {/* PERSONAL — Form 16 etc */}
        <TabsContent value="personal" className="mt-4">
          <Card className="p-4 sm:p-6 border-slate-200 shadow-sm bg-white">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="font-bold font-[Outfit] text-slate-900">Personal & Tax Documents</div>
                <div className="text-[12px] text-slate-500">Generated by HR · download anytime · share for visa, loans, taxes</div>
              </div>
              <span className="text-[10px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full uppercase tracking-wider">{user?.full_name || "Your account"}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {PERSONAL_DOCS.map((d) => {
                const have = personalByCode[d.code];
                const Icon = d.icon;
                return (
                  <div key={d.code} className="p-4 rounded-xl border border-slate-200 bg-white hover:border-violet-200 hover:shadow-md transition" data-testid={`personal-${d.code}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg qh-grad-bg text-white flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 text-sm">{d.label}{d.yearly && <span className="text-[10px] font-bold text-amber-700 ml-1.5">FY {new Date().getFullYear()-1}-{String(new Date().getFullYear()).slice(2)}</span>}</div>
                        <div className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{d.desc}</div>
                      </div>
                    </div>
                    {have ? (
                      <Button variant="outline" size="sm" className="w-full justify-center" data-testid={`download-${d.code}`}>
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                      </Button>
                    ) : (
                      <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                        <span>Not available yet</span>
                        <button className="font-semibold text-violet-700 hover:underline" onClick={() => toast.info("HR will be notified to issue this document")}>Request</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="policy" className="mt-4">
          {byCat.policy.length === 0
            ? <EmptyState icon={FileCheck} title="No policies published" message="HR will publish leave policy, code of conduct, IT policy here." />
            : <Card className="border-slate-200 shadow-sm bg-white">{byCat.policy.map(Row)}</Card>
          }
        </TabsContent>
        <TabsContent value="form" className="mt-4">
          {byCat.form.length === 0
            ? <EmptyState icon={FileText} title="No forms yet" message="Reimbursement, NOC, transfer request and other forms will appear here." />
            : <Card className="border-slate-200 shadow-sm bg-white">{byCat.form.map(Row)}</Card>
          }
        </TabsContent>
        <TabsContent value="letter_template" className="mt-4">
          {byCat.letter_template.length === 0
            ? <EmptyState icon={Award} title="No letter templates yet" message="Offer, appointment, experience and relieving letter templates appear here once HR uploads them." />
            : <Card className="border-slate-200 shadow-sm bg-white">{byCat.letter_template.map(Row)}</Card>
          }
        </TabsContent>
      </Tabs>
    </div>
  );
}
